<template>
  <div>
    <b-row class="search-block mb-3 mt-1">
      <b-col
        class="d-flex"
        xl="8"
        lg="10"
        md="12"
      >
        <local-searcher-clinical
          :identifier-options="identifierScopesForSearch"
          :initial-identifier="identifierScopesForSearch[0]"
          @requestSearch="requestSearch"
          @studySearch="studySearch"
          @patientSearch="patientSearch"
        />
      </b-col>
      <b-col
        class="text-right"
        xl="4"
        lg="2"
        md="12"
      >
        <div
          class="mt-1"
          style="margin-bottom: -1px;"
        >
          <b-button
            v-if="hasOperationInAnyLaboratoryGlobalMixin(requestOperation.CREATE)"
            v-shortkey="['alt', 'n']"
            class="ml-3 btn btn--primary btn-secondary btn-sm"
            @shortkey.prevent="$refs.clinicalSearchModal.show()"
            @click="$refs.clinicalSearchModal.show()"
          >
            <fa-icon
              class="mr-1"
              :icon="['fal', 'plus']"
            />
            {{ $tc('global.request', 1) }}
          </b-button>
        </div>
      </b-col>
    </b-row>
    <div class="grid-container">
      <GridHeader
        v-if="gridApi"
        :gridtitle="rowData.length + ' ' + $tc('global.patients', rowData.length)"
      >
        <div class="pl-1 pr-0 d-flex align-items-center">
          <button
            id="samplesCleanFilters"
            v-tooltip="$t('global.cleanFilter')"
            v-shortkey="['ctrl', 'alt', 'f']"
            type="button"
            class="btn btn__icon btn__icon--primary mx-2 p-0 btn-secondary"
            @shortkey.prevent="cleanFilters"
            @click="cleanFilters"
          >
            <i
              class="far fa-filter"
              aria-hidden="true"
            />
          </button>
        </div>
        <div class="pl-1 pr-0 d-flex align-items-center">
          <button
            v-tooltip="$t('global.export')"
            type="button"
            class="btn btn__icon btn__icon--primary mx-2 p-0 btn-secondary"
          >
            <i
              class="far fa-file-export"
              aria-hidden="true"
            />
          </button>
        </div>
      </GridHeader>
      <div class="is-relative h-100">
        <patient-sidepanel
          v-if="showSidePanel && patientIdSelected"
          ref="patientSidePanel"
          :key="patientIdSelected"
          :patient-id="patientIdSelected"
          @closePatientSidepanel="showSidePanel = false"
        />
        <vt-grid
          class="study-request-grid"
          master-detail
          use-detail-custom-config
          :detail-cell-renderer-params="detailCellRendererParams"
          :row-data="rowData"
          :grid-options="gridOptions"
          :column-defs="columnDefs"
        />
      </div>
    </div>
    <modal-wrapper ref="clinicalSearchModal">
      <clinical-search-patient-modal
        id-modal="clinicalSearchModal"
        @patientSelectedCreateRequest="createRequest($event)"
        @patientSelected="createRequest($event)"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import VtGrid from '@/components/VtGrid/VtGrid'
import LocalSearcherClinical from '@/components/SearcherModuleEntitySelection'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import { RequestOperation } from '@/api/operations/models/request-operation'
import PatientResource from '@/api/resources/PatientResource'
import GridHeader from '@/components/GridHeader'
import clinicalRequestService from '@/modules/clinicalModule/services/clinicalRequestService'
import clinicalRequestPrintService from '@/modules/printModule/services/ClinicalRequestPrintService'
import ClinicalSearchPatientModal from '@/modules/clinicalModule/components/clinical-search-patient-modal/ClinicalSearchPatientModal'
import PatientIdentifierResource from '@/api/resources/PatientIdentifierResource'
import { REQUEST_STATES } from '@/api/resources/entityState/request-state'
import Encoder from '@/utils/encoder'
import PatientSidepanel from '@/components/searcherPatientModal/PatientSidepanel'
import { IDENTIFIER_SCOPES, PATIENT_IDENTIFIER } from '@/api/resources/identifierScopesResource'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { clinicalPatientGridMasterColumnDefs } from '@/modules/clinicalModule/views/patientGrid/column-defs/clinical-patient-grid-master.column-defs'
import { clinicalPatientGridDetailColumnDefs } from '@/modules/clinicalModule/views/patientGrid/column-defs/clinical-patient-grid-detail.column-defs'
import { AgGridRowModelType } from '@/shared/models/ag-grid.model'

const ROW_HEIGHT_NORMAL = 30
const ROW_HEIGHT_NO_DATA = 116

export default {
    name: 'ClinicalPatientGrid',
    components: {
        VtGrid,
        LocalSearcherClinical,
        GridHeader,
        ClinicalSearchPatientModal,
        PatientSidepanel
    },
    data() {
        return {
            patientIdSelected: null,
            showSidePanel: false,
            encoder: new Encoder(),
            detailCellRendererParams: {},
            rowData: [],
            gridApi: null,
            requestOperation: RequestOperation,
            columnDefs: clinicalPatientGridMasterColumnDefs
        }
    },
    computed: {
        gridOptions() {
            return {
                pagination: false,
                rowModelType: AgGridRowModelType.CLIENT_SIDE,
                getContextMenuItems: this.getContextMenuItems,
                floatingFilter: false,
                sideBar: gridSideBarConfig,
                onRowClicked: this.onRowClicked,
                onRowDoubleClicked: this.goToPatientDetail,
                onGridReady: this.onGridReady
            }
        },
        identifierScopesForSearch() {
            return [
                { scopes: [IDENTIFIER_SCOPES.CENTER_PATIENT, IDENTIFIER_SCOPES.PERSON], icon: 'fal fa-user-circle', text: this.$tc('component.searcher.patient'), id: PATIENT_IDENTIFIER },
                { scopes: [IDENTIFIER_SCOPES.REQUEST], icon: 'fal fa-file-medical', text: this.$tc('global.request'), errorMessage: this.$tc('clinical.error.validIdentifierRequest') },
                { scopes: [IDENTIFIER_SCOPES.CASE], icon: 'fal fa-chart-network', text: this.$tc('global.case'), errorMessage: this.$tc('clinical.error.validIdentifierCase') }
            ]
        }
    },
    async mounted() {
        await this.addPatientIdentifiersColumns()
        await this.setDetailCellRendererParams()
        if (this.$route.params.patients) {
            this.rowData = this.$route.params.patients
        }
    },
    methods: {
        async getRequestReportPDF(requestId, caseLabel) {
            const blob =
                await this.$_toCallUnique(
                    clinicalRequestPrintService.downloadRequestReportPdf([requestId]))
            if (blob !== null) {
                this.$_openFile({
                    blob,
                    newWindowTabTitle: `${caseLabel} - ${this.$tc('global.report')}`
                })
            }
        },
        onRowClicked({ data }) {
            const isSame =
                this.patientIdSelected &&
                data.patId === this.patientIdSelected
            if (isSame) {
                this.showSidePanel = false
                return
            }
            this.patientIdSelected = data.patId
            this.showSidePanel = true
        },
        getContextMenuItems(params) {
            if (params?.node) {
                return [
                    {
                        name: this.$tc('component.patient.searcher.createRequest'),
                        action: () => this.createRequest(params.node.data.patId),
                        cssClasses: !this.hasOperationInAnyLaboratoryGlobalMixin(this.requestOperation.CREATE) ? ['not-show'] : ''
                    },
                    {
                        name: this.$tc('clinical.grid.viewRequests'),
                        action: () => this.goToPatientDetail(params.node),
                        cssClasses: !this.hasOperationInAnyLaboratoryGlobalMixin(this.requestOperation.CREATE) ? ['not-show'] : ''
                    }
                ]
            }
            return []
        },
        async setDetailCellRendererParams() {
            this.detailCellRendererParams = {
                detailGridOptions: {
                    pagination: false,
                    rowModelType: AgGridRowModelType.CLIENT_SIDE,
                    floatingFilter: false,
                    sideBar: gridSideBarConfig,
                    getContextMenuItems: (params) => {
                        if (params?.node) {
                            return [
                                {
                                    name: this.$tc('global.export'),
                                    subMenu: [
                                        {
                                            name: this.$tc('global.contextMenu.excel'),
                                            action: () => {
                                                const onlySelected = !!(params.api.getSelectedNodes().length)
                                                params.api.exportDataAsExcel({
                                                    onlySelected,
                                                    processCellCallback: ({ column, value }) =>
                                                        this.$_processCellOutput(column, value)
                                                })
                                            }
                                        }
                                    ]
                                },
                                {
                                    name: this.$tc('registry.btn.viewAnaReport'),
                                    action: () => this.getRequestReportPDF(params.node.data.id, params.node.data.caseStudy.casCasenumber),
                                    cssClasses: params.node.data.status.code === REQUEST_STATES.COMPLETED.code ? '' : ['not-show']
                                }
                            ]
                        }
                        return []
                    },
                    columnDefs: clinicalPatientGridDetailColumnDefs
                },
                getDetailRowData: async params => {
                    const data = await this.getMasterDetailRowData(params?.node.data.patId)
                    params.successCallback(data)
                }
            }
        },
        async getMasterDetailRowData(patientId) {
            const response =
                await this.$_toCallSecure(
                    clinicalRequestService.paginator(
                        null,
                        null,
                        `/clinical/laboratories/patients/${patientId}/requests/previous`))
            if (response) {
                return response.content
            }
            return []
        },
        goToPatientDetail({ data }) {
            this.$router.push({
                name: 'clinicalPatientDetail',
                params: {
                    id: data.patId
                }
            })
        },
        async addPatientIdentifiersColumns() {
            const response =
                await this.$_toCallSecure(
                    PatientIdentifierResource.getPersonIdentifiers())
            if (response) {
                const personIdentifiers = response
                if (personIdentifiers.length) {
                    personIdentifiers.forEach((identifier) => {
                        this.columnDefs.push({
                            type: 'text',
                            field: identifier.code,
                            colId: identifier.code,
                            headerName: identifier.name,
                            valueGetter: ({ data }) => data.personIdentifiers[identifier.code.toString()]?.identifier
                        })
                    })
                }
            }
        },
        async createRequest(patId) {
            const response =
                await this.$_toCallSecure(
                    PatientResource.findById(patId))
            if (response) {
                this.$router.push({
                    name: 'newRequest',
                    params: {
                        initPatient: response
                    }
                })
            }
        },
        cleanFilters() {
            this.gridApi.setFilterModel(null)
        },
        onGridReady({ api }) {
            this.gridApi = api

            this.gridApi.setColumnDefs(
                this.$_filterPatientColumns(
                    this.columnDefs,
                    'patMiddleName',
                    'patSurname2'))
            api.sizeColumnsToFit()
        },

        async requestSearch(search) {
            const response =
                await this.$_toCallUnique(
                    clinicalRequestService.searchRequestByLabel(search),
                    error => this.$_showErrorModal(
                        error?.response?.data?.code === this.$apiExceptions.ENTITY_NOT_FOUND
                            ? this.$tc('component.searcher.error.noRequest')
                            : undefined))
            if (response) {
                this.$router.push({
                    name: 'editRequest',
                    params: {
                        requestId: response.requestId,
                        labId: response.laboratoryId
                    }
                })
            }
        },
        async studySearch(searchInformation) {
            const response =
                await this.$_toCallUnique(
                    clinicalRequestService.searchRequestByCaseNumber(searchInformation),
                    error => this.$_showErrorModal(
                        error?.response?.data?.code === this.$apiExceptions.ENTITY_NOT_FOUND
                            ? this.$tc('component.searcher.error.noCase')
                            : undefined))
            if (response) {
                this.$router.push({
                    name: 'editRequest',
                    params: {
                        requestId: response.requestId,
                        labId: response.laboratoryId
                    }
                })
            }
        },
        async patientSearch(searchInformation) {
            const params = {
                size: PAGINATOR_BIG_PAGE_SIZE,
                id: searchInformation.identifierSearch,
                surname1: searchInformation.patientSurname1Search,
                surname2: searchInformation.patientSurmname2Search,
                middleName: searchInformation.patientMiddlenameSearch,
                name: searchInformation.patientNameSearch,
                centerCode: searchInformation.center,
                identifierType: searchInformation.type
            }
            const response =
                await this.$_toCallUnique(
                    PatientResource.paginator(params),
                    error => this.$_showErrorModal(
                        error?.response?.data?.code === this.$apiExceptions.RESULT_LIMIT_EXCEEDED
                            ? this.$tc('component.patient.searcher.tooManySearchResults')
                            : undefined))
            if (response) {
                const patients = response.content
                if (!patients.length && searchInformation.identifierSearch) {
                    this.$_showErrorModal(
                        this.$tc('component.patient.searcher.noResultsWithIdentifier',
                            { identifier: searchInformation.identifierName }))
                } else if (!patients.length) {
                    this.$_showErrorModal(this.$tc('component.patient.searcher.noResults'))
                } else if (patients.length === 1) {
                    this.$router.push({
                        name: 'clinicalPatientDetail',
                        params: {
                            id: patients[0].patId
                        }
                    })
                } else {
                    this.rowData = patients
                }
            }
        }
    }
}
</script>

<style scoped>
.study-request-grid {
    width: 100%;
    height: 100%;
}
</style>
